import { usePortfolioData } from '@/hooks/use-portfolio-data';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Github, Star, GitBranch, GitCommit, ExternalLink, Code } from 'lucide-react';

export default function GithubActivity() {
    const { data: portfolioData, isLoading } = usePortfolioData();

    if (isLoading || !portfolioData) return null;

    const stats = portfolioData.githubStats;

    return (
        <section id="github" className="py-24 relative overflow-hidden">
            {/* Background decorations */}
            <div className="absolute inset-0 bg-gradient-to-b from-background via-primary/5 to-background"></div>

            <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                <div className="flex flex-col items-center mb-16">
                    <div className="inline-flex items-center px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4 animate-fade-in">
                        <span className="animate-pulse mr-2">✦</span> Open Source
                    </div>
                    <h2 className="text-4xl md:text-5xl font-bold mb-6 text-center tracking-tight animate-slide-up">
                        GitHub <span className="bg-gradient-text">Engineering</span>
                    </h2>
                    <div className="w-24 h-1.5 bg-gradient-to-r from-primary to-secondary rounded-full mb-6 animate-slide-up animate-delay-100"></div>
                    <p className="text-lg text-muted-foreground max-w-2xl mx-auto text-center animate-slide-up animate-delay-200">
                        Showcasing my commitment to open-source and continuous software development.
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* GitHub Stats Card */}
                    <Card className="lg:col-span-1 rounded-2xl border-muted/30 shadow-lg bg-card/80 dark:bg-background/50 backdrop-blur-sm card-hover">
                        <CardHeader className="text-center pb-2">
                            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Github className="h-8 w-8 text-primary" />
                            </div>
                            <h3 className="text-2xl font-bold">Contribution Overview</h3>
                        </CardHeader>
                        <CardContent className="space-y-6 pt-4">
                            <div className="grid grid-cols-2 gap-4 text-center">
                                <div className="p-4 bg-muted/30 rounded-xl">
                                    <div className="text-2xl font-bold text-primary">{stats.repositories}</div>
                                    <div className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">Repos</div>
                                </div>
                                <div className="p-4 bg-muted/30 rounded-xl">
                                    <div className="text-2xl font-bold text-secondary">{stats.commits}</div>
                                    <div className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">Commits</div>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <h4 className="text-sm font-bold flex items-center gap-2 text-foreground">
                                    <Star className="h-4 w-4 text-accent" />
                                    Primary Languages
                                </h4>
                                <div className="flex flex-wrap gap-2">
                                    {stats.languages.map(lang => (
                                        <Badge
                                            key={lang}
                                            variant="secondary"
                                            className="px-3 py-1 bg-black text-white dark:bg-secondary/20 dark:text-secondary-foreground border-none transition-all hover:bg-primary hover:text-white"
                                        >
                                            {lang}
                                        </Badge>
                                    ))}
                                </div>
                            </div>

                            <div className="pt-4">
                                <a
                                    href={portfolioData.github}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-full"
                                >
                                    <button className="w-full flex items-center justify-center gap-2 py-3 bg-primary text-white font-bold rounded-xl shadow-lg hover:shadow-primary/20 transition-all hover:scale-[1.02]">
                                        <Github className="h-5 w-5" />
                                        Visit GitHub Profile
                                    </button>
                                </a>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Top Repositories Grid */}
                    <div className="lg:col-span-2 space-y-6">
                        <h3 className="text-2xl font-bold flex items-center gap-3">
                            <GitBranch className="h-6 w-6 text-primary" />
                            Featured Repositories
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {stats.topProjects.map((repo, idx) => (
                                <Card
                                    key={repo.name}
                                    className="rounded-2xl border-muted/30 shadow-md bg-background/50 backdrop-blur-sm hover:border-primary/30 transition-all group overflow-hidden"
                                    style={{ animationDelay: `${idx * 150}ms` }}
                                >
                                    <CardContent className="p-6">
                                        <div className="flex justify-between items-start mb-4">
                                            <div className="p-2 bg-primary/5 rounded-lg group-hover:bg-primary/10 transition-colors">
                                                <Code className="h-6 w-6 text-primary" />
                                            </div>
                                            <a
                                                href={repo.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-muted-foreground hover:text-primary transition-colors"
                                            >
                                                <ExternalLink className="h-5 w-5" />
                                            </a>
                                        </div>
                                        <h4 className="text-lg font-extrabold mb-2 group-hover:text-primary transition-colors">
                                            {repo.name}
                                        </h4>
                                        <p className="text-xs text-muted-foreground font-mono truncate mb-4">
                                            {repo.url}
                                        </p>
                                        <div className="flex items-center gap-4 text-xs font-semibold text-muted-foreground">
                                            <div className="flex items-center gap-1">
                                                <Star className="h-3 w-3 text-accent" /> Custom
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <GitCommit className="h-3 w-3 text-secondary" /> Active
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>

                        {/* Mock Contribution Graph Placeholder */}
                        <Card className="rounded-2xl border-muted/30 shadow-lg bg-background/50 backdrop-blur-sm overflow-hidden p-6 relative">
                            <div className="flex justify-between items-center mb-6">
                                <h4 className="text-sm font-bold flex items-center gap-2">
                                    <GitCommit className="h-4 w-4 text-secondary" />
                                    Code Contributions
                                </h4>
                                <div className="text-[10px] text-muted-foreground font-mono flex gap-1">
                                    <span>Less</span>
                                    <div className="flex gap-1">
                                        <div className="w-2.5 h-2.5 bg-muted rounded-sm" />
                                        <div className="w-2.5 h-2.5 bg-primary/20 rounded-sm" />
                                        <div className="w-2.5 h-2.5 bg-primary/40 rounded-sm" />
                                        <div className="w-2.5 h-2.5 bg-primary/60 rounded-sm" />
                                        <div className="w-2.5 h-2.5 bg-primary rounded-sm" />
                                    </div>
                                    <span>More</span>
                                </div>
                            </div>

                            <div className="flex gap-1 animate-pulse-subtle">
                                {[...Array(52)].map((_, i) => (
                                    <div key={i} className="flex flex-col gap-1 flex-1">
                                        {[...Array(7)].map((_, j) => {
                                            const rand = Math.random();
                                            const intensity = rand > 0.8 ? 'bg-primary' : rand > 0.5 ? 'bg-primary/40' : rand > 0.2 ? 'bg-primary/10' : 'bg-muted/30';
                                            return <div key={j} className={`aspect-square w-full rounded-[1px] ${intensity}`} />;
                                        })}
                                    </div>
                                ))}
                            </div>
                            <p className="text-center text-[10px] text-muted-foreground mt-4 italic font-mono uppercase tracking-widest">
                                Data generated from GitHub activity records
                            </p>
                        </Card>
                    </div>
                </div>
            </div>
        </section>
    );
}
